<template>
  <b-modal
    id="bulk-edit-wizard-modal"
    v-model="localIsOpen"
    :title="$t('bulk_edit.modal_title')"
    centered
    no-close-on-backdrop
    no-close-on-esc
    header-bg-variant="dark"
    header-text-variant="light"
    size="xl"
    @hidden="onHidden"
  >
    <!-- Step indicator (icons + arrows, mirrors the unit wizard; clickable) -->
    <div class="bulk-step-indicator mb-1">
      <template v-for="(step, index) in stepTitles">
        <div
          :class="{ 'bulk-step': true, active: currentStep === index }"
          :key="'step-' + index"
          @click="goToStep(index)"
        >
          <div class="step-content">
            <feather-icon :icon="stepIcons[index]" size="25" />
            <div class="step-label text-capitalize">{{ step }}</div>
          </div>
        </div>

        <!-- Arrow after each step except the last one -->
        <div
          v-if="index < stepTitles.length - 1"
          class="arrow"
          :key="'arrow-' + index"
        >
          <feather-icon icon="ChevronRightIcon" size="30" />
        </div>
      </template>
    </div>

    <hr class="mt-0" />

    <!-- Scrollable body: keeps the title + step indicator always visible -->
    <div class="bulk-modal-body">
      <p class="text-muted small mb-2">
        {{ $t("bulk_edit.only_filled_fields") }}
      </p>

      <!-- Step 1: info (the original bulk fields) -->
      <div v-show="currentStep === 0">
        <bulk-edit-fields-form
          :fields="fields"
          :form-data="formData"
          :catalog-options="catalogOptions"
        />
      </div>

      <!-- Step 2: additional info (mirrors AdditionalInfoStep) -->
      <div v-show="currentStep === 1">
        <validation-observer ref="refFormObserver" #default="{ handleSubmit }">
          <b-form
            class="mt-1"
            @submit.prevent="handleSubmit(onSubmitAdditionalInfo)"
          >
            <b-row class="mx-0 px-0">
              <b-col cols="5" class="pl-0">
                <!-- Key -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('unit.key')"
                  rules="required"
                >
                  <b-form-group
                    class="text-capitalize"
                    :label="$t('unit.key')"
                    label-for="bulk-additional-info-key"
                  >
                    <b-form-input
                      id="bulk-additional-info-key"
                      v-model="item.key"
                      :state="getValidationState(validationContext)"
                      trim
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="5" class="pl-0">
                <!-- Value -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('unit.value')"
                  rules="required"
                >
                  <b-form-group
                    class="text-capitalize"
                    :label="$t('unit.value')"
                    label-for="bulk-additional-info-value"
                  >
                    <b-form-input
                      id="bulk-additional-info-value"
                      v-model="item.value"
                      :state="getValidationState(validationContext)"
                      trim
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="2" class="pl-0">
                <div class="form-group mb-0">
                  <label class="d-block">&nbsp;</label>
                  <b-button type="submit" variant="primary">
                    {{ addButtonTitle }}
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

        <div>
          <p>{{ $t("unit.additional_info") }}</p>
          <b-table-simple v-if="additionalInfoList.length" class="table">
            <b-thead>
              <b-tr>
                <b-th class="text-center">
                  {{ $tc("actions.name", 2) }}
                </b-th>
                <b-th class="text-center">
                  {{ $t("unit.key") }}
                </b-th>
                <b-th class="text-center">
                  {{ $t("unit.value") }}
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(info, index) in additionalInfoList" :key="index">
                <b-td class="text-center">
                  <b-button
                    :id="`bulk-additional-info-actions-${index}`"
                    tabindex="0"
                    variant="white"
                    class="px-1 btn-y"
                  >
                    <feather-icon
                      data-cy="actions"
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </b-button>
                  <b-popover
                    :target="`bulk-additional-info-actions-${index}`"
                    triggers="focus"
                    placement="bottom"
                    custom-class="popover-p-0"
                    variant="white"
                  >
                    <b-list-group class="m-0">
                      <b-list-group-item
                        @click="edit(info, index)"
                        class="border-0"
                        button
                      >
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50">
                          {{ $t("actions.edit") }}
                        </span>
                      </b-list-group-item>
                      <b-list-group-item
                        @click="remove(index)"
                        class="border-0"
                        button
                      >
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50">
                          {{ $t("actions.delete") }}
                        </span>
                      </b-list-group-item>
                    </b-list-group>
                  </b-popover>
                </b-td>
                <b-td class="text-center">
                  {{ info.key }}
                </b-td>
                <b-td class="text-center">
                  {{ info.value }}
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <b-alert v-else show variant="warning" class="p-1">
            {{ $t("unit.no_data") }}
          </b-alert>

          <p class="text-muted small mb-0">
            {{ $t("bulk_edit.additional_info_hint") }}
          </p>
        </div>
      </div>

      <!-- Step 3: emergency contacts (mirrors EmergencyContactsStep) -->
      <div v-show="currentStep === 2">
        <validation-observer ref="refContactsObserver">
          <b-form>
            <div
              v-for="(contact, index) in emergencyContactList"
              :key="index"
            >
              <b-row class="mb-2">
                <b-col cols="2">
                  <validation-provider
                    #default="validationContext"
                    :vid="`bulk_section_${index}`"
                    :name="$t('unit.section')"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('unit.section')"
                      :state="getValidationState(validationContext)"
                    >
                      <v-select
                        v-model="contact.section"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="catalogOptions.teamsOptions"
                        :reduce="(val) => val.label"
                        :input-id="`bulk_section_${index}`"
                        append-to-body
                        :calculate-position="withPopper"
                      />
                      <b-form-invalid-feedback
                        :state="getValidationState(validationContext)"
                      >
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="2">
                  <validation-provider
                    #default="validationContext"
                    :vid="`bulk_phone_number_${index}`"
                    :name="$t('unit.phone_number')"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('unit.phone_number')"
                      :state="getValidationState(validationContext)"
                    >
                      <b-form-input
                        v-model="contact.phone_number"
                        :state="getValidationState(validationContext)"
                        trim
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="2">
                  <validation-provider
                    #default="validationContext"
                    :vid="`bulk_name_${index}`"
                    :name="$t('unit.name')"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('unit.name')"
                      :state="getValidationState(validationContext)"
                    >
                      <b-form-input
                        v-model="contact.name"
                        :state="getValidationState(validationContext)"
                        trim
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="4">
                  <validation-provider
                    #default="validationContext"
                    :vid="`bulk_description_${index}`"
                    :name="$t('unit.description')"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('unit.description')"
                      :state="getValidationState(validationContext)"
                    >
                      <b-form-input
                        v-model="contact.description"
                        :state="getValidationState(validationContext)"
                        trim
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="auto">
                  <div class="form-group mb-0">
                    <label class="d-block">&nbsp;</label>
                    <b-button
                      variant="danger"
                      @click="removeContact(index)"
                      class="mr-2"
                    >
                      {{ $t("unit.remove_contact") }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </div>

            <b-button variant="secondary" @click="addContact">
              {{ $t("unit.add_contact") }}
            </b-button>
          </b-form>
        </validation-observer>

        <p class="text-muted small mt-1 mb-0">
          {{ $t("bulk_edit.emergency_contact_hint") }}
        </p>
      </div>
    </div>
    <!-- /bulk-modal-body -->

    <!-- Footer -->
    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$emit('close')"
        :disabled="isSubmitting"
        class="text-capitalize mr-auto"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        v-if="currentStep > 0"
        variant="outline-primary"
        @click="goToStep(currentStep - 1)"
        :disabled="isSubmitting"
        class="text-capitalize"
      >
        {{ $t("actions.previous") }}
      </b-button>
      <b-button
        v-if="currentStep < stepTitles.length - 1"
        variant="outline-primary"
        @click="goToStep(currentStep + 1)"
        :disabled="isSubmitting"
        class="text-capitalize"
      >
        {{ $t("actions.next") }}
      </b-button>
      <b-button
        variant="primary"
        @click="onApply"
        :disabled="isSubmitting || !hasChanges"
        class="text-capitalize"
      >
        <b-spinner small v-if="isSubmitting" class="mr-50" />
        <span>
          {{ $t("bulk_edit.apply_changes", { count: selectedCount }) }}
        </span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";
import { required } from "@validations";
import { ref, computed } from "@vue/composition-api";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import BulkEditFieldsForm from "@/components/BulkEdit/BulkEditFieldsForm.vue";

const ADDITIONAL_INFO_KEY = "additional_info";
const EMERGENCY_CONTACT_KEY = "emergency_contact";

const blankItem = () => ({ key: null, value: null });

const blankContact = () => ({
  section: null,
  name: "",
  phone_number: "",
  description: "",
});

export default {
  name: "BulkEditWizardModal",
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BulkEditFieldsForm,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    selectedCount: {
      type: Number,
      default: 0,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    catalogOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit, parent }) {
    const { refFormObserver, getValidationState } = formValidation();
    const refContactsObserver = ref(null);
    const currentStep = ref(0);

    // Same steps and titles as the unit wizard (only the ones holding
    // bulk-editable data: info, additional info and emergency contacts).
    const stepTitles = computed(() => [
      parent.$i18n.t("users.info"),
      parent.$i18n.t("unit.additional_info"),
      parent.$i18n.t("unit.emergency_contacts"),
    ]);

    // Same icons as the unit wizard step indicator.
    const stepIcons = ["ToolIcon", "InfoIcon", "PhoneIcon"];

    const localIsOpen = computed({
      get() {
        return props.isOpen;
      },
      set(value) {
        if (!value) {
          emit("close");
        }
      },
    });

    // --- Additional info (key/value pairs upserted on every unit) ---
    // The row being added or edited; `editIndex` null means "adding".
    const item = ref(blankItem());
    const editIndex = ref(null);

    const additionalInfoList = computed(
      () => props.formData[ADDITIONAL_INFO_KEY] || []
    );

    const addButtonTitle = computed(() =>
      editIndex.value === null
        ? parent.$i18n.t("actions.add")
        : parent.$i18n.t("actions.edit")
    );

    // An empty list goes back to null so the field counts as untouched.
    const setAdditionalInfo = (list) => {
      props.formData[ADDITIONAL_INFO_KEY] = list.length ? list : null;
    };

    const onSubmitAdditionalInfo = () => {
      const list = [...additionalInfoList.value];
      const entry = { key: item.value.key, value: item.value.value };

      if (editIndex.value !== null) {
        list.splice(editIndex.value, 1, entry);
      } else {
        list.push(entry);
      }
      setAdditionalInfo(list);

      item.value = blankItem();
      editIndex.value = null;
      refFormObserver.value.reset();
    };

    const edit = (info, index) => {
      item.value = { ...info };
      editIndex.value = index;
    };

    const remove = (index) => {
      const list = [...additionalInfoList.value];
      list.splice(index, 1);
      setAdditionalInfo(list);

      // Drop the pending edit if its row is the one being removed.
      if (editIndex.value === index) {
        item.value = blankItem();
        editIndex.value = null;
      }
    };

    // --- Emergency contacts (the list replaces the one on every unit) ---
    const emergencyContactList = computed(
      () => props.formData[EMERGENCY_CONTACT_KEY] || []
    );

    // An empty list goes back to null so the field counts as untouched.
    const setEmergencyContacts = (list) => {
      props.formData[EMERGENCY_CONTACT_KEY] = list.length ? list : null;
    };

    const addContact = () => {
      setEmergencyContacts([...emergencyContactList.value, blankContact()]);
    };

    const removeContact = (index) => {
      const list = [...emergencyContactList.value];
      list.splice(index, 1);
      setEmergencyContacts(list);
    };

    const goToStep = (step) => {
      currentStep.value = step;
    };

    // Empty strings and empty arrays count as untouched (see useBulkEdit).
    const hasChanges = computed(() =>
      Object.values(props.formData).some(
        (v) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !(Array.isArray(v) && v.length === 0)
      )
    );

    // Contacts are free rows: validate them before submitting so no blank
    // contact travels in the payload.
    const onApply = async () => {
      if (emergencyContactList.value.length) {
        const isValid = await refContactsObserver.value.validate();
        if (!isValid) {
          currentStep.value = 2;
          return;
        }
      }
      emit("submit");
    };

    const onHidden = () => {
      currentStep.value = 0;
      item.value = blankItem();
      editIndex.value = null;
      emit("close");
    };

    return {
      required,
      refFormObserver,
      refContactsObserver,
      getValidationState,
      currentStep,
      stepTitles,
      stepIcons,
      localIsOpen,
      item,
      additionalInfoList,
      addButtonTitle,
      onSubmitAdditionalInfo,
      edit,
      remove,
      emergencyContactList,
      addContact,
      removeContact,
      goToStep,
      hasChanges,
      onApply,
      onHidden,
      withPopper: popperHelper.withPopper,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/variables/_variables";

.modal-header .bg-dark {
  background-color: var(--main-dark) !important;
}

// Fixed-height scroll area so the modal title + step indicator stay visible.
.bulk-modal-body {
  height: 60vh;
  overflow-y: auto;
}

// Step indicator (mirrors WizardStepIndicator)
.bulk-step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.bulk-step {
  display: flex;
  align-items: center;
  margin-right: 10px;
  cursor: pointer;

  .step-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-right: 10px;
    opacity: 0.6;
    transition: opacity 0.2s ease, color 0.2s ease;
  }

  &.active {
    color: $secondary;

    .step-content {
      opacity: 1;
    }
  }
}

.step-label {
  margin-top: 5px;
}

.arrow {
  display: flex;
  align-items: center;
  margin-right: 10px;
  color: #b9b9c3;
}
</style>
